import {
  addRxPlugin,
  createRxDatabase,
  removeRxDatabase,
  type RxDatabase,
  type RxStorage,
} from 'rxdb';
import {
  disableVersionCheck,
  setPremiumFlag,
} from 'rxdb-premium/plugins/shared';
import { getRxStorageIndexedDB } from 'rxdb-premium/plugins/storage-indexeddb';
import { disableWarnings, RxDBDevModePlugin } from 'rxdb/plugins/dev-mode';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv';
import { modifiersCollections } from '../../features/appointmentProposalOptionModifiers/modifiers.collection';
import { optionProductsCollections } from '../../features/appointmentProposalOptionProducts/products.collection';
import { optionsCollections } from '../../features/appointmentProposalOptions/options.collection';
import { sowsCollections } from '../../features/appointmentProposalOptionSows/sows.collection';
import { proposalsCollections } from '../../features/appointmentProposals/proposals.collection';
import { appointmentsCollections } from '../../features/appointments/appointments.collection';
import { membershipDataCollections } from '../../features/membershipData/membershipData.collection';
import { orgContentCollections } from '../../features/organizationPresentationContent/orgContent.collection';
import { pricebookCollections } from '../../features/pricebook/pricebook.collection';
import { OPTED_IN_KEY, SCHEMA_FINGERPRINT_KEY } from './storageKeys';
import { type AirshipCollections, type CollectionName } from './types';

setPremiumFlag();
addRxPlugin(RxDBLeaderElectionPlugin);
addRxPlugin(RxDBQueryBuilderPlugin);

const isDev = import.meta.env.DEV;
if (isDev) {
  addRxPlugin(RxDBDevModePlugin);
}

if (!isDev) {
  disableVersionCheck();
  disableWarnings();
}

const getStorage = (): RxStorage<unknown, unknown> => {
  const base = getRxStorageIndexedDB();
  return isDev ? wrappedValidateAjvStorage({ storage: base }) : base;
};

export const DB_NAME = 'airship-service-management';
export const INIT_LOCK_NAME = 'airship-rxdb-init';
export const TAB_PRESENCE_LOCK_NAME = 'airship-tab-presence';

type LockOptions = {
  lockTimeout: number;
};

export const waitForTabVisible = (): Promise<void> => {
  const doc = typeof document !== 'undefined' ? document : undefined;
  if (!doc || !('visibilityState' in doc)) {
    return Promise.resolve();
  }

  const isVisible = doc.visibilityState !== 'hidden';
  if (isVisible) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const onVisibilityChange = (): void => {
      if (doc.visibilityState !== 'hidden') {
        doc.removeEventListener('visibilitychange', onVisibilityChange);
        resolve();
      }
    };
    doc.addEventListener('visibilitychange', onVisibilityChange);
  });
};

export const onTabHidden = (cb: () => void): (() => void) => {
  const doc = typeof document !== 'undefined' ? document : undefined;
  if (!doc || !('visibilityState' in doc)) {
    return () => undefined;
  }
  const handle = (): void => {
    if (doc.visibilityState === 'hidden') {
      cb();
    }
  };
  doc.addEventListener('visibilitychange', handle);
  return () => doc.removeEventListener('visibilitychange', handle);
};

export const withInitLock = async <T>(
  options: LockOptions,
  fn: () => Promise<T>
): Promise<T> => {
  if (typeof navigator === 'undefined' || !navigator.locks?.request) {
    return fn();
  }
  const abort = new AbortController();
  const timeoutId = setTimeout(() => abort.abort(), options.lockTimeout);
  try {
    return await navigator.locks.request(
      INIT_LOCK_NAME,
      { signal: abort.signal },
      () => {
        clearTimeout(timeoutId);
        return fn();
      }
    );
  } catch (err) {
    if (
      err instanceof DOMException &&
      err.name === 'AbortError' &&
      abort.signal.aborted
    ) {
      return fn();
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const acquireTabPresenceLock = (): Promise<void> => {
  if (typeof navigator === 'undefined' || !navigator.locks?.request) {
    return Promise.resolve();
  }
  return navigator.locks.request<void>(
    TAB_PRESENCE_LOCK_NAME,
    { mode: 'shared' },
    () =>
      new Promise<void>(() => {
        // never resolves; held until tab close
      })
  );
};

export const queryLocks = (): Promise<LockManagerSnapshot | null> => {
  if (typeof navigator === 'undefined' || !navigator.locks?.query) {
    return Promise.resolve(null);
  }
  return navigator.locks.query();
};

export const createDatabase = (): Promise<RxDatabase<AirshipCollections>> =>
  createRxDatabase<AirshipCollections>({
    name: DB_NAME,
    storage: getStorage(),
    eventReduce: true,
    multiInstance: true,
    closeDuplicates: true,
    cleanupPolicy: {
      minimumDeletedTime: 1000 * 60 * 60 * 24 * 31, // one month
      minimumCollectionAge: 1000 * 60, // 60 seconds
      awaitReplicationsInSync: true,
      waitForLeadership: true,
    },
  });

export const allCollections = {
  ...appointmentsCollections,
  ...proposalsCollections,
  ...optionsCollections,
  ...optionProductsCollections,
  ...modifiersCollections,
  ...sowsCollections,
  ...pricebookCollections,
  ...membershipDataCollections,
  ...orgContentCollections,
};

export const ALL_COLLECTION_NAMES = Object.keys(
  allCollections
) as CollectionName[];

type HandleDBAction = (db: RxDatabase<AirshipCollections>) => Promise<void>;

export const addAirshipCollections: HandleDBAction = async (db) => {
  await db.addCollections(allCollections);
};

const removeStorageKeys = (storage: Storage) => {
  const keysToRemove = ['airship:offline', 'airship-cache'] as const;
  const excludedKeys = [OPTED_IN_KEY] as const;
  const foundKeys = new Set<string>();

  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (
      key &&
      keysToRemove.some((k) => key.startsWith(k)) &&
      !excludedKeys.some((k) => key.endsWith(k))
    ) {
      foundKeys.add(key);
    }
  }

  for (const key of foundKeys) {
    storage.removeItem(key);
  }
};

export const removeDatabaseAndKeys = async (): Promise<void> => {
  await removeRxDatabase(DB_NAME, getStorage());
  removeStorageKeys(localStorage);
  removeStorageKeys(sessionStorage);
};

export const schemaFingerprint = (
  collections: Record<string, { schema: { version: number } }>
): string =>
  Object.entries(collections)
    .map(([name, creator]) => `${name}:${creator.schema.version}`)
    .sort()
    .join('|');

export const wipeIfSchemaChanged = async (): Promise<void> => {
  const fingerprint = schemaFingerprint(allCollections);
  if (localStorage.getItem(SCHEMA_FINGERPRINT_KEY) === fingerprint) {
    return;
  }

  try {
    await removeDatabaseAndKeys();
  } finally {
    localStorage.setItem(SCHEMA_FINGERPRINT_KEY, fingerprint);
  }
};
